import type { UserAccount, UserRole, LoginResult, SendCodeResult, VerificationCode } from '@/types/auth';
import { hasAdminRole } from '@/types/auth';
import { defaultNickname } from '@/utils/defaultNickname';
import { DEFAULT_AVATAR_KEY } from '@/types/system-avatar';

const USERS_KEY = 'mock_users';
const CODES_KEY = 'mock_codes';

/** mock 专用扩展:模拟密码比对字段(真实 API 口径为脱敏 passwordSet,不下发 hash) */
type MockUser = UserAccount & { passwordHash: string };

function loadUsers(): MockUser[] {
  const raw = localStorage.getItem(USERS_KEY);
  if (!raw) return [getSuperAdmin(), getTestLeader(), getTestManager(), getTestStaff()];
  try {
    const parsed = JSON.parse(raw);
    return parsed.map((u: MockUser) => ({
      ...u,
      createdAt: new Date(u.createdAt),
      lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : undefined,
    }));
  } catch {
    return [getSuperAdmin(), getTestLeader(), getTestManager(), getTestStaff()];
  }
}

function saveUsers(users: MockUser[]) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

const MOCK_ORG_ID = '00000000-0000-0000-0000-000000000001';

function getSuperAdmin(): MockUser {
  return {
    id: '1',
    mobile: '13800138000',
    displayName: '系统管理员',
    role: 'super_admin',
    status: 'normal',
    organizationId: MOCK_ORG_ID,
    passwordHash: 'mock_hash_admin123',
    isFirstLogin: true,
    createdAt: new Date('2026-04-28'),
    lastLoginAt: new Date('2026-04-28'),
  };
}

function getTestLeader(): MockUser {
  return {
    id: '2',
    mobile: '13800138001',
    displayName: '测试负责人',
    role: 'leader' as UserRole,
    status: 'normal',
    organizationId: MOCK_ORG_ID,
    passwordHash: '',
    wechatOpenId: 'wx_test_leader',
    isFirstLogin: false,
    createdAt: new Date('2026-04-28'),
    lastLoginAt: new Date('2026-04-28'),
  };
}

function getTestManager(): MockUser {
  return {
    id: '3',
    mobile: '13800138002',
    displayName: '测试台账管理员',
    role: 'manager' as UserRole,
    status: 'normal',
    organizationId: MOCK_ORG_ID,
    passwordHash: '',
    wechatOpenId: 'wx_test_manager',
    isFirstLogin: false,
    createdAt: new Date('2026-04-28'),
    lastLoginAt: new Date('2026-04-28'),
  };
}

function getTestStaff(): MockUser {
  return {
    id: '4',
    mobile: '13800138003',
    displayName: '测试员工',
    role: 'standard',
    status: 'normal',
    organizationId: MOCK_ORG_ID,
    passwordHash: '',
    wechatOpenId: 'wx_test_staff',
    isFirstLogin: false,
    createdAt: new Date('2026-04-28'),
    lastLoginAt: new Date('2026-04-28'),
  };
}

function loadCodes(): VerificationCode[] {
  const raw = localStorage.getItem(CODES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return parsed.map((c: VerificationCode) => ({
      ...c,
      createdAt: new Date(c.createdAt),
      expiresAt: new Date(c.expiresAt),
    }));
  } catch {
    return [];
  }
}

function saveCodes(codes: VerificationCode[]) {
  localStorage.setItem(CODES_KEY, JSON.stringify(codes));
}

function generateCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

function generateToken(userId: string): string {
  return `mock_jwt_${userId}_${Date.now()}`;
}

function generateId(): string {
  return String(Date.now());
}

// 测试手机号列表（预置账号）
const TEST_MOBILES = ['13800138000', '13800138001', '13800138002', '13800138003'];

export async function sendVerificationCode(mobile: string): Promise<SendCodeResult> {
  await new Promise((resolve) => setTimeout(resolve, 800));

  const codes = loadCodes();
  const now = new Date();
  const recent = codes.filter(
    (c) => c.mobile === mobile && now.getTime() - c.createdAt.getTime() < 60000
  );
  if (recent.length > 0) {
    return { success: false, error: '请求过于频繁，请稍后再试' };
  }

  // 测试账号使用固定验证码 123456，方便测试
  const code = TEST_MOBILES.includes(mobile) ? '123456' : generateCode();
  const record: VerificationCode = {
    mobile,
    code,
    createdAt: now,
    expiresAt: new Date(now.getTime() + 5 * 60 * 1000),
    attemptCount: 0,
  };
  codes.push(record);
  saveCodes(codes);

  console.log(`[Mock SMS] Verification code for ${mobile}: ${code}`);
  if (TEST_MOBILES.includes(mobile)) {
    console.log(`[Mock SMS] 测试账号 ${mobile} 的验证码固定为 123456`);
  }

  return { success: true, message: '验证码已发送' };
}

export async function login(params: {
  method: string;
  mobile?: string;
  code?: string;
  username?: string;
  password?: string;
  authCode?: string;
}): Promise<LoginResult> {
  await new Promise((resolve) => setTimeout(resolve, 600));

  const users = loadUsers();
  let user: MockUser | undefined;
  let requireSetup = false;

  if (params.method === 'mobile_code') {
    const mobile = params.mobile || '';
    const code = params.code || '';

    const codes = loadCodes();
    const now = new Date();
    const record = codes.find(
      (c) => c.mobile === mobile && c.expiresAt > now
    );

    if (!record) {
      return { success: false, error: '验证码错误或已过期' };
    }

    if (record.code !== code) {
      record.attemptCount += 1;
      saveCodes(codes);
      if (record.attemptCount >= 3) {
        return { success: false, error: '验证码错误次数过多，请重新获取' };
      }
      return { success: false, error: '验证码错误' };
    }

    user = users.find((u) => u.mobile === mobile);
    if (!user) {
      user = {
        id: generateId(),
        mobile,
        // 与后端 NewAccountDefaults.fillMissing 同口径：注册即补默认昵称与默认头像
        displayName: defaultNickname(mobile),
        avatarUrl: DEFAULT_AVATAR_KEY,
        role: 'standard',
        isFirstLogin: true,
        passwordHash: '',
        createdAt: new Date(),
        lastLoginAt: new Date(),
      };
      users.push(user);
      saveUsers(users);
      requireSetup = true;
    } else {
      requireSetup = user.isFirstLogin;
      user.lastLoginAt = new Date();
      saveUsers(users);
    }
  } else if (params.method === 'password') {
    const username = params.username || '';
    const password = params.password || '';

    user = users.find(
      (u) =>
        hasAdminRole(u.role) &&
        (u.mobile === username || u.displayName === username)
    );

    if (!user || user.passwordHash !== `mock_hash_${password}`) {
      return { success: false, error: '用户名或密码错误' };
    }

    // 管理员不需要进入完善信息页面，直接进管理后台
    requireSetup = false;
    if (user.isFirstLogin) {
      user.isFirstLogin = false;
    }
    user.lastLoginAt = new Date();
    saveUsers(users);
  } else if (params.method === 'wechat_qr' || params.method === 'wechat_mobile') {
    const openId = params.authCode || `wx_${Date.now()}`;

    // 测试账号映射：特定 openId 自动匹配预置角色
    const testRoleMap: Record<string, string> = {
      wx_test_leader: 'leader',
      wx_test_manager: 'manager',
      wx_test_staff: 'standard',
    };

    user = users.find((u) => u.wechatOpenId === openId);

    if (!user) {
      const testRole = testRoleMap[openId];
      const newId = generateId();
      user = {
        id: newId,
        mobile: '',
        wechatOpenId: openId,
        // 与后端同口径：预设测试账号有展示名，真实新用户在「用户+ID 末 4 位」之外没有昵称来源
        displayName: testRole
          ? `测试${testRole === 'leader' ? '单位领导' : testRole === 'manager' ? '台账管理员' : '员工'}`
          : defaultNickname(newId),
        avatarUrl: DEFAULT_AVATAR_KEY,
        role: (testRole as UserRole) || 'standard',
        isFirstLogin: !testRole,
        passwordHash: '',
        createdAt: new Date(),
        lastLoginAt: new Date(),
      };
      users.push(user);
      saveUsers(users);
      requireSetup = !testRole;
    } else {
      requireSetup = user.isFirstLogin;
      user.lastLoginAt = new Date();
      saveUsers(users);
    }
  }

  if (!user) {
    return { success: false, error: '登录失败' };
  }

  const token = generateToken(user.id);
  let redirectTo: string;

  if (requireSetup) {
    redirectTo = '/setup';
  } else if (hasAdminRole(user.role)) {
    redirectTo = '/admin';
  } else {
    redirectTo = '/dashboard';
  }

  return {
    success: true,
    user,
    token,
    // 修复登录审查#13：补齐 refresh token 与有效期语义，mock 会话可跨页面刷新持久
    refreshToken: `mock_refresh_${user.id}`,
    expiresIn: 24 * 3600,
    redirectTo,
    requireSetup,
  };
}

export async function logout(token: string): Promise<{ success: boolean }> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  console.log(`[Mock] logout token: ${token}`);
  return { success: true };
}

export function updateUser(user: UserAccount): void {
  const users = loadUsers();
  const idx = users.findIndex((u) => u.id === user.id);
  if (idx >= 0) {
    // 合并更新,保留 mock 侧的 passwordHash 模拟字段
    users[idx] = { ...users[idx], ...user };
    saveUsers(users);
  }
}
